import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Phone, ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { sendOtp, verifyOtp } from "../lib/api";
import { WhatsAppIcon } from "./WhatsAppButton";

const LoginModal = () => {
  const { loginOpen, closeLogin, login, pendingRedirect } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loginOpen) {
      setStep("phone"); setPhone(""); setName(""); setOtp(""); setDemoOtp(""); setError(""); setLoading(false);
    }
  }, [loginOpen]);

  if (!loginOpen) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (phone.length !== 10 || loading) return;
    setLoading(true); setError("");
    try {
      const res = await sendOtp(phone);
      setDemoOtp(res.otp || "");
      setStep("otp");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6 || loading) return;
    setLoading(true); setError("");
    try {
      const res = await verifyOtp(phone, otp, name);
      login({ phone: res.phone, name: res.name, token: res.token });
      closeLogin();
      navigate(pendingRedirect || "/my-orders");
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4" data-testid="login-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeLogin} />
      <div className="relative w-full max-w-md bg-[#FFF8F0] rounded-3xl bakery-shadow-lg p-7 fade-up">
        <button onClick={closeLogin} className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full hover:bg-[#F6E7DA]" data-testid="login-modal-close">
          <X size={20} className="text-[#6B4226]" />
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#25D366] grid place-items-center text-white mx-auto mb-4">
            <WhatsAppIcon size={26} />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#3A2417]">Login with WhatsApp</h2>
          <p className="text-sm text-[#B07A5C] mt-1">
            {step === "phone" ? "We'll send a one-time password to verify you" : `Enter the 6-digit code sent to +91 ${phone}`}
          </p>
        </div>

        {step === "phone" ? (
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#6B4226]">Your Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Priya Sharma"
                className="w-full mt-1.5 h-12 px-4 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]"
                data-testid="login-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#6B4226]">Mobile Number</label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-[#6B4226] text-sm">
                  <Phone size={16} className="text-[#B07A5C]" /> +91
                </span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit number"
                  inputMode="numeric"
                  className="w-full h-12 pl-[74px] pr-4 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]"
                  data-testid="login-phone-input"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <button
              type="submit"
              disabled={phone.length !== 10 || loading}
              className={`w-full h-12 rounded-full font-medium flex items-center justify-center gap-2 transition-colors ${phone.length === 10 && !loading ? "bg-[#25D366] text-white hover:bg-[#1eb956]" : "bg-[#EADBCB] text-[#B07A5C] cursor-not-allowed"}`}
              data-testid="login-send-otp-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <WhatsAppIcon size={18} />}
              Send OTP
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            {demoOtp && (
              <div className="bg-[#FCEFE3] border border-[#EADBCB] rounded-xl p-3 text-center" data-testid="login-demo-otp">
                <span className="text-xs text-[#B07A5C]">Demo mode — your OTP is </span>
                <span className="font-display text-lg font-bold text-[#D46A8A] tracking-widest">{demoOtp}</span>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-[#6B4226]">Enter OTP</label>
              <div className="relative mt-1.5">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B07A5C]" size={18} />
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit code"
                  inputMode="numeric"
                  autoFocus
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-white border border-[#EADBCB] text-lg tracking-[0.4em] outline-none focus:border-[#D46A8A]"
                  data-testid="login-otp-input"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <button
              type="submit"
              disabled={otp.length !== 6 || loading}
              className={`w-full h-12 rounded-full font-medium flex items-center justify-center gap-2 transition-colors ${otp.length === 6 && !loading ? "bg-[#D46A8A] text-white hover:bg-[#c25a7b]" : "bg-[#EADBCB] text-[#B07A5C] cursor-not-allowed"}`}
              data-testid="login-verify-otp-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
              Verify & Continue
            </button>
            <button
              type="button"
              onClick={() => { setStep("phone"); setOtp(""); setError(""); }}
              className="w-full text-sm text-[#B07A5C] hover:text-[#D46A8A] flex items-center justify-center gap-1"
              data-testid="login-change-number-btn"
            >
              <ArrowLeft size={14} /> Change number
            </button>
          </form>
        )}
        <p className="text-[11px] text-[#B07A5C] text-center mt-5">
          Demo mode: OTP is shown on screen (no paid WhatsApp provider connected yet).
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
